import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  Search,
  Download,
  Mail,
  MoreHorizontal,
  CheckCircle,
  XCircle,
  Edit,
  Trash2,
  ArrowUpDown,
  Filter,
  Calendar,
  Users,
  BookOpen,
  Loader2,
} from "lucide-react";
import { EventsService } from "@/api/events";
import { getCurrentClientData } from "@/services/authServices";
import { PaymentStatusBadge } from "@/components/general/StatusBadge";
import { EmailModal } from "@/components/general/EmailModal";
import { AttendeeDetailsModal } from "./components/AttendeeDetailsModal";
import { unixToDate } from "@/lib/utils";
import { TableLoadingSkeleton } from "@/components/SkeletonLoaders/TableLoadingSkeleton";
import LoadingSpinner from "@/components/general/LoadingSpinner";

// Sample attendees data - in a real app, this would come from an API
const mockAttendees = [
  {
    id: "1",
    firstName: "John",
    lastName: "Doe",
    email: "john.doe@example.com",
    phone: "+1 (555) 123-4567",
    ticketType: "VIP Ticket",
    company: "Tech Innovators Inc.",
    jobTitle: "CTO",
    registrationDate: "2023-11-15T14:30:00",
    status: "confirmed",
    checkedIn: false,
    paymentStatus: "paid",
  },
  {
    id: "2",
    firstName: "Jane",
    lastName: "Smith",
    email: "jane.smith@example.com",
    phone: "+1 (555) 987-6543",
    ticketType: "Standard Ticket",
    company: "Digital Solutions",
    jobTitle: "Product Manager",
    registrationDate: "2023-11-16T10:15:00",
    status: "confirmed",
    checkedIn: true,
    paymentStatus: "paid",
  },
  {
    id: "3",
    firstName: "Robert",
    lastName: "Johnson",
    email: "robert.j@example.com",
    phone: "+1 (555) 456-7890",
    ticketType: "Early Bird",
    company: "Innovate Labs",
    jobTitle: "Software Architect",
    registrationDate: "2023-11-14T09:45:00",
    status: "pending",
    checkedIn: false,
    paymentStatus: "pending",
  },
  {
    id: "4",
    firstName: "Sarah",
    lastName: "Williams",
    email: "sarah.w@example.com",
    phone: "+1 (555) 789-0123",
    ticketType: "VIP Ticket",
    company: "Growth Ventures",
    jobTitle: "Marketing Director",
    registrationDate: "2023-11-17T15:20:00",
    status: "confirmed",
    checkedIn: false,
    paymentStatus: "paid",
  },
  {
    id: "5",
    firstName: "Michael",
    lastName: "Brown",
    email: "michael.b@example.com",
    phone: "+1 (555) 321-6547",
    ticketType: "Standard Ticket",
    company: "Tech Solutions Global",
    jobTitle: "Data Scientist",
    registrationDate: "2023-11-18T11:30:00",
    status: "confirmed",
    checkedIn: false,
    paymentStatus: "paid",
  },
];

export interface AttendeeProps {
  attendee_id: string;
  full_name: string;
  email: string;

  company: string;
  job_title: string;

  ticket_type: string;
  registration_date: string;
  checkin_status: string;
  payment_status: string;
}
// Mock event data
interface EventDataInterface {
  id: string | number;
  event_title: string;
  date: string;
  location: string;
}

const ManageAttendees = () => {
  const { eventId } = useParams();
  const { toast } = useToast();
  
  const { getEvents1, listDashboardAttendees, exportAttendees } = EventsService;
  const [eventData, setEventData] = useState<EventDataInterface>();
  const [attendees, setAttendees] = useState<AttendeeProps[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [ticketFilter, setTicketFilter] = useState("all");
  const [selectedAttendees, setSelectedAttendees] = useState<AttendeeProps[]>(
    []
  );
  const loggedInUser = getCurrentClientData();
  const [pageLoader, setPageLoader] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingExport, setLoadingExport] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [attendeeDetails, setAttendeeDetails] = useState<any>();
  const [isOpenDetails, setIsOpenDetails] = useState(false);

  useEffect(() => {
    loadAllEventsFn();
    loadDashboardAttendees();
  }, []);

  const loadAllEventsFn = async () => {
    setPageLoader(true);

    try {
      const res: any = await getEvents1(eventId, loggedInUser?.token);

      if (res.statusCode === 200 || res.statusCode === 201) {
        setEventData(res.body.data);
      } else {
        toast({
          title: "Error",
          description:
            res.body.message || "There was an error creating your event.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "There was an error creating your event.",
        variant: "destructive",
      });
      return error;
    } finally {
      setPageLoader(false);
    }
  };

  const loadDashboardAttendees = async () => {
    setLoading(true);

    try {
      const res: any = await listDashboardAttendees(
        eventId,
        loggedInUser?.token
      );

      if (res.statusCode === 200 || res.statusCode === 201) {
        setAttendees(res.body.attendees);
      } else {
        toast({
          title: "Error",
          description:
            res.body.message || "There was an error creating your event.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "There was an error creating your event.",
        variant: "destructive",
      });
      return error;
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setLoadingExport(true);
    try {
      const res: any = await exportAttendees(eventId, loggedInUser?.token);

      if (res.statusCode === 200 || res.statusCode === 201) {
        setLoadingExport(false);
        const csvContent = res.body;
        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;

        // Try to get filename from Content-Disposition header if available
        const contentDisposition =
          res.headers?.["Content-Disposition"] ||
          res.headers?.["content-disposition"];
        const fileNameMatch = contentDisposition?.match(/filename="(.+)"/);
        const filename = fileNameMatch?.[1] || "attendees.csv";

        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        setLoadingExport(false);
        toast({
          title: "Error",
          description:
            res.body.message || "There was an error exporting attendees.",
          variant: "destructive",
        });
      }
    } catch (error) {
      setLoadingExport(false);
      toast({
        title: "Error",
        description: "There was an error exporting attendees.",
        variant: "destructive",
      });
      return error;
    } finally {
      setLoadingExport(false);
    }
  };

  // Filter attendees based on search and filters
  const filteredAttendees =
    // attendees.length > 0 &&
    attendees.filter((attendee) => {
      const matchesSearch =
        attendee.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        attendee.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        attendee.company.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "checkedIn" && attendee.checkin_status) ||
        (statusFilter === "notCheckedIn" && !attendee.checkin_status) ||
        (statusFilter === "pending" && attendee.checkin_status === "pending");

      const matchesTicket =
        ticketFilter === "all" ||
        attendee.ticket_type.toLowerCase().includes(ticketFilter.toLowerCase());

      return matchesSearch && matchesStatus && matchesTicket;
    });

  const toggleSelectAll = () => {
    if (selectedAttendees.length === filteredAttendees.length) {
      setSelectedAttendees([]);
    } else {
      setSelectedAttendees(filteredAttendees);
      // setSelectedAttendees(filteredAttendees.map((a) => a?.full_name));
    }
  };

  // const toggleSelectAttendee = (id: string) => {
  //   if (selectedAttendees.includes(id)) {
  //     setSelectedAttendees(selectedAttendees.filter((a) => a !== id));
  //   } else {
  //     setSelectedAttendees([...selectedAttendees, id]);
  //   }
  // };
  const toggleSelectAttendee = (id: string) => {
    const isSelected = selectedAttendees.some((a) => a.attendee_id === id);

    if (isSelected) {
      setSelectedAttendees(
        selectedAttendees.filter((a) => a.attendee_id !== id)
      );
    } else {
      const newAttendee = filteredAttendees.filter((a) => a.attendee_id === id);
      setSelectedAttendees([...selectedAttendees, ...newAttendee]);
    }
  };

  const handleBulkAction = (action: string) => {
    if (selectedAttendees.length === 0) {
      toast({
        title: "No attendees selected",
        description:
          "Please select at least one attendee to perform this action.",
        variant: "destructive",
      });
      return;
    }

    // Process the action (in a real app, you would make API calls)
    if (action === "email") {
      // toast({
      //   title: "Email Sent",
      //   description: `Email sent to ${selectedAttendees.length} attendees.`,
      // });
      setIsOpen(true);
    } else if (action === "checkIn") {
      // Update check-in status
      // setAttendees(
      //   attendees.map((a) =>
      //     selectedAttendees.includes(a.attendee_id)
      //       ? { ...a, checkedIn: true }
      //       : a
      //   )
      // );

      toast({
        title: "Check-in Successful",
        description: `${selectedAttendees.length} attendees checked in.`,
      });
    } else if (action === "delete") {
      // Remove attendees
      // setAttendees(
      //   attendees.filter((a) => !selectedAttendees.includes(a.full_name))
      // );
      setSelectedAttendees([]);

      toast({
        title: "Attendees Removed",
        description: `${selectedAttendees.length} attendees removed successfully.`,
      });
    }
  };

  const handleToggleCheckIn = (id: string, currentStatus: boolean) => {
    setAttendees(
      attendees.map((a) =>
        a.full_name === id ? { ...a, checkedIn: !currentStatus } : a
      )
    );

    toast({
      title: currentStatus ? "Check-in Reversed" : "Checked In",
      description: `Attendee has been ${
        currentStatus ? "un-checked" : "checked"
      } in.`,
    });
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      {pageLoader ? (
        <LoadingSpinner />
      ) : (
        <main className="flex-grow bg-gray-50">
          {/* Page Header */}
          <div className="bg-cecure-900 text-white py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div>
                  <h1 className="text-2xl font-bold">Manage Attendees</h1>
                  <div className="flex items-center mt-2 text-sm">
                    <Calendar className="h-4 w-4 mr-1" />
                    <span className="mr-4">{eventData.event_title}</span>
                    <Users className="h-4 w-4 mr-1" />
                    <span>{attendees.length} Registered</span>
                  </div>
                </div>
                <div className="mt-4 md:mt-0 flex flex-wrap gap-3">
                  <Link to={`/dashboard/${eventId}`}>
                    <Button
                      variant="outline"
                      className="bg-white text-cecure-900 hover:bg-gray-100"
                    >
                      Back to Dashboard
                    </Button>
                  </Link>
                  <Button onClick={() => handleBulkAction("email")}>
                    <Mail className="mr-2 h-4 w-4" />
                    Email Attendees
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <Card>
              <CardHeader className="space-y-0 pb-2">
                <CardTitle>Attendee List</CardTitle>
              </CardHeader>
              {loading ? (
                <TableLoadingSkeleton />
              ) : (
                <CardContent>
                  {/* Filters and Search */}
                  <div className="flex flex-col md:flex-row gap-4 mb-6">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                      <Input
                        placeholder="Search attendees..."
                        className="pl-10"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>

                    <div className="flex gap-3 flex-wrap">
                      <Select
                        value={statusFilter}
                        onValueChange={setStatusFilter}
                      >
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Statuses</SelectItem>
                          <SelectItem value="checkedIn">Checked In</SelectItem>
                          <SelectItem value="notCheckedIn">
                            Not Checked In
                          </SelectItem>
                          <SelectItem value="pending">
                            Registration Pending
                          </SelectItem>
                        </SelectContent>
                      </Select>

                      <Select
                        value={ticketFilter}
                        onValueChange={setTicketFilter}
                      >
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Ticket Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Tickets</SelectItem>
                          <SelectItem value="vip">VIP Ticket</SelectItem>
                          <SelectItem value="standard">
                            Standard Ticket
                          </SelectItem>
                          <SelectItem value="early">Early Bird</SelectItem>
                        </SelectContent>
                      </Select>

                      {/* <Button variant="outline" className="gap-2">
                    <Filter className="h-4 w-4" />
                    More Filters
                  </Button> */}

                      <Button
                        variant="outline"
                        className="gap-2"
                        disabled={loadingExport}
                        onClick={handleExport}
                      >
                        {loadingExport ? (
                          <Loader2 />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                        Export
                      </Button>
                    </div>
                  </div>

                  {/* Bulk Actions */}
                  {selectedAttendees.length > 0 && (
                    <div className="bg-muted p-3 rounded-md mb-4 flex items-center justify-between">
                      <div>
                        <span className="font-medium">
                          {selectedAttendees.length} attendees selected
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          disabled={true}
                          size="sm"
                          variant="outline"
                          onClick={() => handleBulkAction("checkIn")}
                        >
                          <CheckCircle className="mr-2 h-4 w-4" />
                          Check-in
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleBulkAction("email")}
                        >
                          <Mail className="mr-2 h-4 w-4" />
                          Email
                        </Button>
                        <Button
                          disabled={true}
                          size="sm"
                          variant="destructive"
                          onClick={() => handleBulkAction("delete")}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Remove
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Attendees Table */}

                  <div className="rounded-md border overflow-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[40px]">
                            <Checkbox
                              checked={
                                selectedAttendees.length ===
                                  filteredAttendees.length &&
                                filteredAttendees.length > 0
                              }
                              onCheckedChange={toggleSelectAll}
                            />
                          </TableHead>
                          <TableHead>
                            <div className="flex items-center">
                              Attendee
                              <ArrowUpDown className="ml-2 h-4 w-4" />
                            </div>
                          </TableHead>
                          <TableHead>Company</TableHead>
                          <TableHead>Ticket Type</TableHead>
                          <TableHead>
                            <div className="flex items-center">
                              Registration Date
                              <ArrowUpDown className="ml-2 h-4 w-4" />
                            </div>
                          </TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Payment</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredAttendees.length > 0 ? (
                          filteredAttendees.map((attendee) => (
                            <TableRow key={attendee.attendee_id}>
                              <TableCell>
                                <Checkbox
                                  checked={selectedAttendees.some(
                                    (obj) =>
                                      obj.attendee_id === attendee.attendee_id
                                  )}
                                  onCheckedChange={() =>
                                    toggleSelectAttendee(attendee.attendee_id)
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                <div>
                                  <div className="font-medium capitalize">
                                    {attendee.full_name}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {attendee.email}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div>
                                  <div>{attendee.company || "N/A"}</div>
                                  <div className="text-sm text-gray-500">
                                    {attendee.job_title}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="capitalize">
                                {attendee.ticket_type}
                              </TableCell>
                              <TableCell>
                                {unixToDate(attendee.registration_date)}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  {attendee?.checkin_status === "pending" ? (
                                    <Badge
                                      variant="outline"
                                      className="bg-yellow-50 text-yellow-700 border-yellow-300"
                                    >
                                      Pending
                                    </Badge>
                                  ) : attendee.checkin_status ? (
                                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                                      Checked In
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline">
                                      Not Checked In
                                    </Badge>
                                  )}
                                </div>
                              </TableCell>

                              <TableCell>
                                <PaymentStatusBadge
                                  status={attendee.payment_status}
                                />
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() =>
                                      handleToggleCheckIn(
                                        attendee.full_name,
                                        attendee.checkin_status === "pending"
                                          ? false
                                          : Boolean(attendee.checkin_status)
                                      )
                                    }
                                    className={
                                      attendee.checkin_status
                                        ? "text-red-600"
                                        : "text-green-600"
                                    }
                                  >
                                    {/* {attendee.checkin_status ? (
                                    <XCircle className="h-4 w-4" />
                                  ) : (
                                    <CheckCircle className="h-4 w-4" />
                                  )} */}
                                  </Button>

                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button size="sm" variant="ghost">
                                        <MoreHorizontal className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem
                                        onClick={() => {
                                          setAttendeeDetails(attendee);
                                          setIsOpenDetails(true);
                                        }}
                                      >
                                        <BookOpen className="mr-2 h-4 w-4" />
                                        View Details
                                      </DropdownMenuItem>
                                      <DropdownMenuItem className="opacity-45">
                                        <Mail className="mr-2 h-4 w-4" />
                                        Send Email
                                      </DropdownMenuItem>
                                      <DropdownMenuItem className="text-red-600 opacity-45">
                                        <Trash2 className="mr-2 h-4 w-4" />
                                        Remove
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={7} className="h-24 text-center">
                              No attendees found.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              )}
            </Card>
          </div>
          <EmailModal
            open={isOpen}
            onOpenChange={setIsOpen}
            allAttendees={attendees.map((attendee) => ({
              id: attendee.attendee_id,
              email: attendee.email,
              full_name: attendee.full_name,
            }))}
            defaultRecipient={selectedAttendees.map((attendee) => ({
              id: attendee.attendee_id,
              email: attendee.email,
              full_name: attendee.full_name,
            }))}
            eventId={eventId}
          />
          {attendeeDetails && (
            <AttendeeDetailsModal
              open={isOpenDetails}
              onOpenChange={setIsOpenDetails}
              onCloseChange={() => setAttendeeDetails(null)}
              attendee={attendeeDetails}
            />
          )}
        </main>
      )}
      <Footer />
    </div>
  );
};

export default ManageAttendees;
